import type {
  CartResponse,
  AddToCartPayload,
  UpdateCartItemPayload,
  RemoveCartItemPayload,
  SubsidyChoices,
  ChequeChoices,
} from "@/features/cart/types/cart.type";
import { fetcher } from "@/shared/lib/fetcher.client";

/**
 * Transforms cart response from API by converting string amounts to numbers
 * API returns decimal values as strings to preserve precision,
 * but the app expects numbers for calculations and formatting
 */
const transformCartResponse = (data: CartResponse): CartResponse => {
  const parseAmount = (value: unknown, fallback = 0): number => {
    const numeric = typeof value === "number"
      ? value
      : value !== null && value !== undefined
        ? Number(value)
        : NaN;

    return Number.isFinite(numeric) ? numeric : fallback;
  };

  const parseTimestampSeconds = (value: unknown): number | null => {
    const numeric = typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : NaN;

    return Number.isFinite(numeric) ? numeric : null;
  };

  const resolveReservationExpiry = (): Date | null => {
    if (data.reserved_until) {
      const fromReservedUntil = new Date(data.reserved_until);
      if (!Number.isNaN(fromReservedUntil.getTime())) {
        return fromReservedUntil;
      }
    }

    const ttlSeconds = parseTimestampSeconds(data.ttl);
    if (ttlSeconds) {
      const fromTtl = new Date(ttlSeconds * 1000);
      if (!Number.isNaN(fromTtl.getTime())) {
        return fromTtl;
      }
    }

    return null;
  };

  const parsedTtl = parseTimestampSeconds(data.ttl);
  const reservationExpiresAt = resolveReservationExpiry();

  const transformAvailableSubsidies = () => {
    if (!data.available_subsidies) return undefined;

    return {
      employee: {
        ...data.available_subsidies.employee,
        potential_amount: parseAmount(data.available_subsidies.employee.potential_amount, 0),
      },
      beneficiaries: data.available_subsidies.beneficiaries.map((b) => ({
        ...b,
        potential_amount: parseAmount(b.potential_amount, 0),
      })),
    };
  };

  const transformAvailableCheques = () => {
    if (!data.available_cheques) return undefined;

    return {
      ...data.available_cheques,
      total_available: parseAmount(data.available_cheques.total_available, 0),
      cheques: data.available_cheques.cheques.map((c) => ({
        ...c,
        available_amount: parseAmount(c.available_amount, 0),
      })),
    };
  };

  return {
    ...data,
    total_amount: parseAmount(data.total_amount, 0),
    total_subsidy_amount: parseAmount(data.total_subsidy_amount, 0),
    total_amount_after_subsidy: parseAmount(
      data.total_amount_after_subsidy,
      parseAmount(data.total_amount, 0),
    ),
    total_cheque_amount: parseAmount(data.total_cheque_amount, 0),
    total_amount_after_subsidy_and_cheque: parseAmount(
      data.total_amount_after_subsidy_and_cheque,
      parseAmount(data.total_amount_after_subsidy, parseAmount(data.total_amount, 0)),
    ),
    ttl: parsedTtl,
    reservation_expires_at: reservationExpiresAt,
    reserved_until: data.reserved_until ?? null,
    subsidy_choices: data.subsidy_choices,
    available_subsidies: transformAvailableSubsidies(),
    cheque_choices: data.cheque_choices,
    available_cheques: transformAvailableCheques(),
    items: (data.items ?? []).map((item) => {
      const quantity = parseAmount(item.quantity, 1);
      const priceAmount = parseAmount(item.price_amount, 0);
      const totalAmount = parseAmount(item.total_amount, priceAmount * quantity);

      return {
        ...item,
        quantity,
        price_amount: priceAmount,
        original_price_amount: parseAmount(item.original_price_amount, priceAmount),
        discount_amount: parseAmount(item.discount_amount, 0),
        total_amount: totalAmount,
      };
    }),
    errors: Array.isArray(data.errors)
      ? data.errors
        .map((msg) => (typeof msg === "string" ? msg : String(msg)))
        .filter(Boolean)
      : undefined,
  };
};

export const cartService = {
  getCart: async (): Promise<CartResponse> => {
    const response = await fetcher.get<CartResponse>("/rce-cart");
    return transformCartResponse(response.data);
  },

  clearCart: async (): Promise<CartResponse> => {
    const response = await fetcher.delete<CartResponse>("/rce-cart");
    return transformCartResponse(response.data);
  },

  addToCart: async (payload: AddToCartPayload): Promise<CartResponse> => {
    const response = await fetcher.put<CartResponse>("/rce-cart/items", payload);
    return transformCartResponse(response.data);
  },

  saveSeatSelection: async (payload: { numero_commande_ticketnet: string, items: AddToCartPayload[] }): Promise<CartResponse> => {
    if (!payload.numero_commande_ticketnet || payload.items.length === 0) {
      throw new Error("Aucune sélection de places à enregistrer.");
    }
    // The minimal API accepts one rate at a time. Stop on the first failure.
    let cart: CartResponse | undefined;
    for (const item of payload.items) {
      const response = await fetcher.put<CartResponse>("/rce-cart/items", {
        ...item,
        numero_commande_ticketnet: payload.numero_commande_ticketnet,
      });
      cart = transformCartResponse(response.data);
    }
    if (!cart) throw new Error("Aucune sélection de places à enregistrer.");
    return cart;
  },

  updateCartItem: async (payload: UpdateCartItemPayload): Promise<CartResponse> => {
    const response = await fetcher.put<CartResponse>("/rce-cart/items", payload);
    return transformCartResponse(response.data);
  },

  removeCartItem: async (payload: RemoveCartItemPayload): Promise<CartResponse> => {
    const response = await fetcher.delete<CartResponse>("/rce-cart/items", { data: payload });
    return transformCartResponse(response.data);
  },

  updateSubsidyChoices: async (payload: SubsidyChoices): Promise<CartResponse> => {
    const response = await fetcher.patch<CartResponse>("/rce-cart/subsidy_choices", { subsidy_choices: payload });
    return transformCartResponse(response.data);
  },

  updateChequeChoices: async (payload: ChequeChoices): Promise<CartResponse> => {
    const response = await fetcher.patch<CartResponse>("/rce-cart/cheque_choices", { cheque_choices: payload });
    return transformCartResponse(response.data);
  },
};
